import FaqGradient from "../assets/faq-gradient.png";
import SeparatorTop from "../assets/separator-top.png";

type ContentEntry = {
    heading: string;
    body: React.ReactNode;
};

const entries: ContentEntry[] = [
    {
        heading: "What is my tenant ID and where do I find it?",
        body: (
            <>
                <p>
                    Your Microsoft 365 tenant ID is the GUID that uniquely identifies your tenant in Entra ID. Think of
                    it as your Microsoft fingerprint — one string that tells every service exactly which environment
                    it’s dealing with.
                </p>
                <p>
                    You can find it in the Microsoft Entra admin center under Overview → Tenant information. Or run{" "}
                    <code>Get-MgOrganization</code> in PowerShell and it’ll be in the output. Either way, it takes 30
                    seconds.
                </p>
                <p>
                    ShareGate uses this same ID to know exactly which tenant it’s connected to. No names. No guesswork.
                    Just the GUID that matches what Microsoft sees.
                </p>
            </>
        ),
    },
    {
        heading: "Why it matters when doing MS365 tenant-to-tenant migration",
        body: (
            <>
                <p>
                    In a{" "}
                    <a href="https://sharegate.com/solutions/tenant-to-tenant-migration" target="_blank" rel="noreferrer">
                        tenant-to-tenant migration
                    </a>
                    , the tenant ID is the address. Every service, every API call, every authentication flow uses it to
                    know which environment is which.
                </p>
                <p>
                    Mix up source and destination and you’re not just looking at a failed migration. You’re looking at
                    content landing in the wrong tenant. That’s the kind of mistake that ends up in a postmortem — and a
                    very uncomfortable conversation with your CISO.
                </p>
                <p>
                    Read the full{" "}
                    <a
                        href="https://sharegate.com/blog/microsoft-365-tenant-to-tenant-migration-tools"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Microsoft 365 tenant-to-tenant migration guide
                    </a>{" "}
                    before you start. Or check the{" "}
                    <a href="https://help.sharegate.com/en/" target="_blank" rel="noreferrer">
                        ShareGate documentation for IT specialists
                    </a>{" "}
                    if you’re already mid-project.
                </p>
            </>
        ),
    },
    {
        heading: "The details: every place tenant ID shows up",
        body: (
            <table className="faq-table">
                <thead>
                    <tr>
                        <th>Topic</th>
                        <th>Description</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Authentication and consent scoping</td>
                        <td>
                            Registering an app or granting admin consent in Entra ID? The tenant ID binds those
                            permissions to <em>your</em> tenant — not someone else’s. In ShareGate, it makes sure the
                            connection targets the right source or destination, not just any M365 tenant that happens to
                            accept the credentials.
                        </td>
                    </tr>
                    <tr>
                        <td>Source vs. destination disambiguation</td>
                        <td>
                            In a tenant-to-tenant move, you’re authenticated to two environments at once. The tenant ID
                            is how the migration engine keeps them straight. Mix them up and content gets read from — or
                            written to — the wrong tenant.
                        </td>
                    </tr>
                    <tr>
                        <td>API routing and throttling</td>
                        <td>
                            Microsoft’s Graph API and SharePoint REST API route requests based on tenant context. The
                            tenant ID makes sure your calls hit the right backend, and that throttling and quota limits
                            track against the correct org. Not your neighbor’s.
                        </td>
                    </tr>
                    <tr>
                        <td>Audit trail and compliance</td>
                        <td>
                            Every operation logged during a migration — who moved what, when, from where — is stamped
                            with the tenant ID. That’s what makes post-migration audits, data residency validation, and
                            regulatory compliance (GDPR, SOC 2) actually defensible.
                        </td>
                    </tr>
                    <tr>
                        <td>Conditional Access and security policies</td>
                        <td>
                            Cross-tenant operations can trigger Conditional Access policies. Knowing both tenant IDs
                            upfront lets you configure trust relationships via Microsoft’s Cross-Tenant Access Settings
                            before you start — not after you hit an authentication failure at 11pm on migration night.
                        </td>
                    </tr>
                    <tr>
                        <td>Rollback and recovery scoping</td>
                        <td>
                            If something goes wrong, your rollback plan needs to be scoped to the right tenant. Tenant
                            IDs in your migration runbook are what stop a recovery action from accidentally touching the
                            wrong environment.
                        </td>
                    </tr>
                </tbody>
            </table>
        ),
    },
    {
        heading: "Do I need a tenant ID to set up ShareGate?",
        body: (
            <>
                <p>Short answer: usually no. But you’ll want it in three situations:</p>
                <ul className="content-list">
                    <li>
                        <strong>Multi-tenant scenarios</strong> — when you’re working across more than one environment
                        and need to be explicit about which one you’re targeting
                    </li>
                    <li>
                        <strong>PowerShell scripting</strong> — commands like <code>Connect-MicrosoftOnline</code> and{" "}
                        <code>Connect-Tenant</code> accept a <code>-TenantId</code> parameter so you can specify exactly
                        which tenant to connect to
                    </li>
                    <li>
                        <strong>Tenant-to-tenant migrations</strong> — where you’re authenticating to both source and
                        destination, and ambiguity is not your friend
                    </li>
                </ul>
                <p>
                    For everything else, ShareGate handles authentication without an explicit tenant ID in most standard
                    flows.
                </p>
                <p>
                    Browse{" "}
                    <a href="https://sharegate.com/category/migration" target="_blank" rel="noreferrer">
                        ShareGate migration articles
                    </a>{" "}
                    for setup guides, PowerShell tips, and real-world migration walkthroughs.
                </p>
            </>
        ),
    },
    {
        heading: "What is the difference between my MS tenant ID and my ShareGate workspace ID?",
        body: (
            <>
                <p>Two different identifiers. Two different worlds.</p>
                <p>
                    Your <strong>Microsoft tenant ID</strong> is the GUID that identifies your Microsoft 365 / Entra
                    tenant. It lives on Microsoft’s side.
                </p>
                <p>
                    Your <strong>ShareGate workspace ID</strong> is ShareGate’s identifier for your workspace and
                    subscription — what used to be the license key. It’s how ShareGate manages access and activations
                    within ShareGate Home. It lives on our side.
                </p>
                <p>They don’t need to match. They never will. And that’s fine.</p>
            </>
        ),
    },
];

type FAQProps = { children?: React.ReactNode };

export const FAQ = ({ children }: FAQProps) => {
    return (
        <section className="faq-section">
            <img className="faq-section__bg" src={FaqGradient} alt="" aria-hidden="true" />
            <div className="faq-container">
                <h2 className="faq-title">Everything you need to know about your tenant ID</h2>
                {entries.map((entry, i) => (
                    <article key={i} className="content-block">
                        <h3 className="content-block__heading">{entry.heading}</h3>
                        <div className="content-block__body">{entry.body}</div>
                    </article>
                ))}
            </div>
            {children && <div className="faq-section__cta">{children}</div>}
            <img className="faq-section__separator faq-section__separator--bottom" src={SeparatorTop} alt="" aria-hidden="true" />
        </section>
    );
};
